import { useState, useCallback, useMemo, useContext } from "react";
import { Message } from "../interfaces/chat.interface";
import { ChatContext } from "@/store/chat.store";
import { CHAT_CONFIG } from "../constants/chat.constant";

/**
 * Chat state interface containing all chat-related state values
 */
export interface ChatState {
  // Message state
  content: string;
  currentMessage: Message | undefined;
  activeConversation: Array<Message> | null;
  streamingConversation: Array<Message> | null;
  streamingConversationId: string | null;
  
  // UI state
  isLoading: boolean;
  isResponseGenerating: boolean;
  isTyping: boolean;
  showUpcoming: boolean;
  
  // Model settings
  modelTemperature: string;
  modelEffort: string;
  selectedModel: string;
  
  // Content management
  contentMap: Map<string, string>;
  filesMap: Map<string, File[]>;
  activeMessageIndex: number | null;
  
  // Computed values
  displayConversation: Array<Message> | null;
}

/**
 * Chat state actions interface containing all state update methods
 */
export interface ChatStateActions {
  setContent: (content: string) => void;
  setCurrentMessage: (message: Message | undefined) => void;
  setActiveConversation: (conversation: Array<Message> | null) => void;
  setStreamingConversation: (conversation: Array<Message> | null) => void;
  setStreamingConversationId: (id: string | null) => void;
  setIsLoading: (loading: boolean) => void;
  setIsResponseGenerating: (generating: boolean) => void;
  setIsTyping: (typing: boolean) => void;
  setShowUpcoming: (show: boolean) => void;
  setModelTemperature: (temperature: string) => void;
  setModelEffort: (effort: string) => void;
  setSelectedModel: (model: string) => void;
  setContentMap: (map: Map<string, string>) => void;
  setFilesMap: (map: Map<string, File[]>) => void;
  setActiveMessageIndex: (index: number | null) => void;
  
  // Content management helpers
  updateContentForConversation: (conversationId: string, content: string) => void;
  getContentForConversation: (conversationId: string) => string;
  clearContentForConversation: (conversationId: string) => void;
  
  // File management helpers
  updateFilesForConversation: (conversationId: string, files: File[]) => void;
  getFilesForConversation: (conversationId: string) => File[];
  clearFilesForConversation: (conversationId: string) => void;
  
  // Computed values
  getDisplayConversation: (selectedConversationId?: string) => Array<Message> | null;
}

/**
 * useChatState hook manages comprehensive chat state including messages, UI state,
 * model settings, content/file persistence, and computed values. Integrates with
 * ChatContext for global state and provides local state management for conversation
 * data, streaming, and content/file maps.
 * 
 * @hook
 * @returns {ChatState & ChatStateActions} Combined state and actions object
 * 
 * @example
 * ```tsx
 * const chatState = useChatState();
 * 
 * // Access state
 * const { content, activeConversation, isResponseGenerating } = chatState;
 * 
 * // Update state
 * chatState.setContent('New message');
 * chatState.setActiveConversation(messages);
 * 
 * // Content persistence
 * chatState.updateContentForConversation(conversationId, 'draft');
 * const draft = chatState.getContentForConversation(conversationId);
 * ```
 */
export const useChatState = (): ChatState & ChatStateActions => {
  const { state, dispatch } = useContext(ChatContext);
  const { content, isResponseGenerating } = state;
  const setContent = useCallback((value: string) => {
    dispatch({ field: "content", value });
  }, [dispatch]);
  const setIsResponseGenerating = useCallback((value: boolean) => {
    dispatch({ field: "isResponseGenerating", value });
  }, [dispatch]);


  const [currentMessage, setCurrentMessage] = useState<Message | undefined>();
  const [activeConversation, setActiveConversation] = useState<Array<Message> | null>([]);
  const [streamingConversation, setStreamingConversation] = useState<Array<Message> | null>(null);
  const [streamingConversationId, setStreamingConversationId] = useState<string | null>(null);
  
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isTyping, setIsTyping] = useState<boolean>(false);
  const [showUpcoming, setShowUpcoming] = useState(false);
  
  const [modelTemperature, setModelTemperature] = useState(String(CHAT_CONFIG.TEMPERATURE.DEFAULT));
  const [modelEffort, setModelEffort] = useState(String(CHAT_CONFIG.REASONING_EFFORT.DEFAULT));
  const [selectedModel, setSelectedModel] = useState<string>(CHAT_CONFIG.MODELS.DEFAULT[0]);
  const [contentMap, setContentMap] = useState<Map<string, string>>(new Map());
  const [filesMap, setFilesMap] = useState<Map<string, File[]>>(new Map());
  const [activeMessageIndex, setActiveMessageIndex] = useState<number | null>(null);

  // Computed display conversation - merges active and streaming
  const displayConversation = useMemo(() => {
    return streamingConversationId && streamingConversation
      ? streamingConversation
      : activeConversation;
  }, [streamingConversationId, streamingConversation, activeConversation]);

  // Computed display conversation function for external use
  const getDisplayConversation = useCallback((selectedConversationId?: string) => {
    return streamingConversationId === selectedConversationId && streamingConversation
      ? streamingConversation
      : activeConversation;
  }, [streamingConversationId, streamingConversation, activeConversation]);

  // Content management helpers
  const updateContentForConversation = useCallback((conversationId: string, content: string) => {
    setContentMap((prev) => new Map(prev).set(conversationId, content));
  }, []);

  const getContentForConversation = useCallback((conversationId: string) => {
    return contentMap.get(conversationId) || "";
  }, [contentMap]);

  const clearContentForConversation = useCallback((conversationId: string) => {
    setContentMap((prev) => {
      const newMap = new Map(prev);
      newMap.set(conversationId, "");
      return newMap;
    });
  }, []);

  // File management helpers
  const updateFilesForConversation = useCallback((conversationId: string, files: File[]) => {
    setFilesMap((prev) => new Map(prev).set(conversationId, files));
  }, []);

  const getFilesForConversation = useCallback((conversationId: string) => {
    return filesMap.get(conversationId) || [];
  }, [filesMap]);

  const clearFilesForConversation = useCallback((conversationId: string) => {
    setFilesMap((prev) => {
      const newMap = new Map(prev);
      newMap.delete(conversationId);
      return newMap;
    });
  }, []);

  return {
    // State
    content,
    currentMessage,
    activeConversation,
    streamingConversation,
    streamingConversationId,
    isLoading,
    isResponseGenerating,
    isTyping,
    showUpcoming,
    modelTemperature,
    modelEffort,
    selectedModel,
    contentMap,
    filesMap,
    activeMessageIndex,
    displayConversation,
    
    // Actions
    setContent,
    setCurrentMessage,
    setActiveConversation,
    setStreamingConversation,
    setStreamingConversationId,
    setIsLoading,
    setIsResponseGenerating,
    setIsTyping,
    setShowUpcoming,
    setModelTemperature,
    setModelEffort,
    setSelectedModel,
    setContentMap,
    setFilesMap,
    setActiveMessageIndex,
    
    // Computed values
    getDisplayConversation,
    
    // Content management helpers
    updateContentForConversation,
    getContentForConversation,
    clearContentForConversation,
    
    // File management helpers
    updateFilesForConversation,
    getFilesForConversation,
    clearFilesForConversation,
  };
};
